import { State, Action, StateContext, Selector, NgxsOnInit, Store } from '@ngxs/store';
import { AuthStateModel } from './auth.state.model';
import { 
  LoginWithBiodigital,
  LoginSuccess,
  LoginFailure,
  GetUserProfile,
  Logout,
  LogoutSuccess,
  IsLoggedIn,
  IsLoggedInSuccess,
  IsLoggedInFailure,
  GetUserProfileSuccess,
  GetUserProfileFailure, 
  CheckSession,
  CheckProfile,
  CheckProfileSuccess,
  CheckProfileFailure,
  GetEnvironmentSettings,
  GetEnvironmentSettingsSuccess,
  GetEnvironmentSettingsFailure,
  ResetPassword,
  ResetPasswordSuccess,
  ResetPasswordFailure,
  SignUp,
  SignUpSuccess,
  SignUpFailure,
  LoginWithGoogle,
  LoginWithFacebook,
  SetUserLocale,
  SetUserLocaleSuccess,
  CheckLocale,
  GetUserApps,
  SetSelectedUserApp,
  SetUserApps,
  CheckSelectedUserApp,
  SetUserMessageViewed,
  SetUserMessageViewedSuccess,
  SetUserMessageViewedFail,
  ResetAuth,
  GetUserMessage,
  GetUserMessageFail,
  GetUserMessageSuccess,
  LogoutFailure,
  VerifyFail,
  UpdateUserProfile,
  UpdateUserProfileFailure,
  UpdateUserProfileSuccess,
  UpdatePassword,
  UpdatePasswordSuccess,
  UpdatePasswordFailure,
  DeleteUserProfile,
  DeleteUserProfileFailure,
  DeleteUserProfileSuccess,
  SendUserMessage,
  SendUserMessageSuccess,
  SendUserMessageFail,
  GetUserRoles
} from './auth.actions';
import { AuthService, HumanVerificationModel, Verification } from '../../auth.service';
import { SetOrganizations, ResetOrganizations } from 'src/app/organizations/state/organizations.actions';
import { finalize, filter, tap, map, mergeMap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { LOCALE_ID, Inject, Injectable } from '@angular/core';
import { LocalStorageEnum, StorageService } from 'src/app/shared/storage.service';
import { locales, defaultLocale } from 'src/app/locale-config.module';
import { ResetBookmarks } from 'src/app/bookmarks/state/bookmarks.actions';
import { ResetEditPage } from 'src/app/edit/state/edit.actions';
import { ResetEnginePosition } from 'src/app/engine/state/engine.actions';
import { ResetLabels } from 'src/app/story/state/labels/story.labels.actions';
import { ResetStory } from 'src/app/story/state/story.actions';
import { ResetStoryChapters } from 'src/app/story/state/chapters/story.chapters.actions';
import { LayoutActions } from 'src/app/layout/state/layout.actions';
import { UserMessageService } from 'src/app/shared/user-message.service';
import { ErrorLoggingService, ErrorMessage } from 'src/app/shared/error-logging.service';
import { get, isNil } from 'lodash';
import { GalleryActions } from 'src/app/gallery/state/gallery.actions';
import { environment } from 'src/environments/environment';
import { EnvironmentDeploy } from 'src/environments/models/environment-deploy';
import { OrganizationModel } from 'src/app/organizations/models/organization.model';
import { UserAccountSetting } from '../../models/account-settings.model';
import { LoginResponseModel } from '../../models/login-base-model';
import { SignUpResponseModel } from '../../models/sign-up.model';
import { AppPlatformTypeEnum } from '../../models/user-app.model';
import { ResetSubscriptionState } from '../subscription/subscription.actions';
import { EnvironmentApplication } from 'src/environments/models/environment-application';
import { LinkedinInsightsService } from 'src/app/shared/linkedin-insights.service';
import { FacebookPixelService } from 'src/app/shared/facebook-pixel.service';
import { UserRoleModel } from '../../models/user-role.model';
import { defaultUserRole } from '../../models/permissions/permissions';
import { Observable, of } from 'rxjs';
import { PendoService } from 'src/app/shared/pendo.service';
import { ActivatedRoute } from '@angular/router';

const isStudio = environment.application === EnvironmentApplication.Studio;

const initialState: AuthStateModel = {
  loading: false,
  profileStatus: 'ready',
  loggedIn: null,
  userLocale: null,
  userRoles: []
};

@State<AuthStateModel>({
  name: 'auth',
  defaults: initialState
})
@Injectable()
export class AuthState implements NgxsOnInit {
  /**
   * Selectors
   */
  @Selector()
  static getLoggedIn(state: AuthStateModel) {
    return state.loggedIn;
  }

  @Selector()
  static getEnvironmentSettings(state: AuthStateModel) {
    return state.environmentSettings;
  }

  @Selector()
  static getUserProfile(state: AuthStateModel) {
    return state.userProfile;
  }

  @Selector()
  static getUserRole(state: AuthStateModel): UserRoleModel {
    if (!isNil(state.userProfile)) { // profile ready
      // get user's role within an organization; else default role
      const userRole: UserRoleModel = get(state, 'userProfile.organizations[0].user_role', defaultUserRole);
      return userRole;
    } else { // profile not ready
      return undefined;
    }
  }

  @Selector()
  static getUserRoles(state: AuthStateModel): UserRoleModel[] {
    return state.userRoles;
  }

  @Selector()
  static getUserAccountSettings(state: AuthStateModel): UserAccountSetting[] {
    const settings: UserAccountSetting[] = get(state, 'userProfile.account_settings', []);
    return settings;
  }

  @Selector()
  static getProfileCheck(state: AuthStateModel) {
    return state.profileCheck;
  }

  @Selector()
  static getLoading(state: AuthStateModel) {
    return state.loading;
  }

  @Selector()
  static getProfileUpdating(state: AuthStateModel) {
    return state.profileStatus === 'updating';
  }

  @Selector()
  static getProfileDeleting(state: AuthStateModel) {
    return state.profileStatus === 'deleting';
  }

  @Selector()
  static getUserLocale(state: AuthStateModel) {
    return state.userLocale;
  }

  @Selector()
  static getUserLanguage(state: AuthStateModel): string {
    return get(state, 'userLocale.lang', defaultLocale.lang);
  }

  @Selector()
  static getUserApps(state: AuthStateModel) {
    return get(state, 'userApps', []);
  }

  @Selector()
  static getSelectedUserApp(state: AuthStateModel) {
    const apps = state.userApps || [];
    return apps.find((app) => app.id === state.selectedAppId);
  }

  @Selector()
  static isPrivateUser(state: AuthStateModel) {
    if (isNil(state.userProfile)) {
      return false;
    }
    
    const organizations = <OrganizationModel[]>get(state, 'userProfile.organizations', []);
    return organizations.length === 0;
  }

  @Selector()
  static isPremiumUser(state: AuthStateModel) {
    return get(state, 'userProfile.premium_account_valid') === true;
  }

  /**
   * Actions
   */
  @Action(IsLoggedIn)
  isLoggedIn(context: StateContext<AuthStateModel>, action: IsLoggedIn) {
    context.patchState({ loading: true });

    this.authService.isLoggedIn()
      .pipe(finalize(() => context.patchState({ loading: false })))
      .subscribe(
        (result: { signed_in: boolean }) => {
          if (result.signed_in) {
            context.dispatch(new IsLoggedInSuccess());
          } else {
            context.dispatch(new IsLoggedInFailure());
          }
        });
  }
  
  @Action(IsLoggedInSuccess)
  isLoggedInSuccess(context: StateContext<AuthStateModel>, action: IsLoggedInSuccess) {
    context.patchState({
      loggedIn: true
    });
  }

  @Action(IsLoggedInFailure)
  isLoggedInFailure(context: StateContext<AuthStateModel>, action: IsLoggedInFailure) {
    context.patchState({
      loggedIn: false
    });
  }


  @Action(LoginWithBiodigital)
  login(context: StateContext<AuthStateModel>, action: LoginWithBiodigital) {
    context.patchState({ loading: true });
    
    this.verify('studio_login', action.login.email, context)
      .pipe(
        mergeMap((verification) => {
          return this.authService.loginWithBiodigital({
            ...action.login,
            ...(verification) ? { site_verify_response: verification } : null
          });
        }),
        finalize(() => context.patchState({ loading: false }))
      )
      .subscribe(
        (response: LoginResponseModel) => {
          context.dispatch(new LoginSuccess('biodigital', response));
        },
        (error) => {
          this.logFailure('LOGIN_FAILURE', error);
          context.dispatch(new LoginFailure('biodigital', error, action.login));
        });
  }

  @Action(LoginWithGoogle)
  loginWithGoogle(context: StateContext<AuthStateModel>, action: LoginWithGoogle) {
    context.patchState({ loading: true });

    this.authService.loginWithGoogle(action.login)
      .pipe(finalize(() => context.patchState({ loading: false })))
      .subscribe(
        (response: LoginResponseModel) => {
          context.dispatch(new LoginSuccess('google', response));
        },
        (error: HttpErrorResponse) => {
          if (action.login.groupCodeMode === 1 || action.login.groupInviteMode === 1) {
            this.logFailure('GOOGLE_SIGNUP_FAILURE', error);
          } 
          
          context.dispatch(new LoginFailure('google', error, action.login));
        });
  }

  // Deprecated
  @Action(LoginWithFacebook)
  loginWithFacebook(context: StateContext<AuthStateModel>, action: LoginWithFacebook) {
    context.patchState({ loading: true });

    this.authService.loginWithFacebook(action.login)
      .pipe(finalize(() => context.patchState({ loading: false })))
      .subscribe(
        (response: LoginResponseModel) => {
          context.dispatch(new LoginSuccess('facebook', response));
        },
        (error: HttpErrorResponse) => {
          context.dispatch(new LoginFailure('facebook', error, action.login));
        });
  }

  @Action(LoginSuccess)
  loginSuccess(context: StateContext<AuthStateModel>, action: LoginSuccess) {

    if (isStudio) {
      this.facebookPixel.trackCustom('Sign in');
      this.linkedinInsights.track('Sign in');
      this.pendoService.track('Sign In - Success', { type: action.loginType });
    }

    context.dispatch(new IsLoggedIn());
    context.dispatch(new CheckLocale());
  }

  @Action(LoginFailure)
  loginFailure(context: StateContext<AuthStateModel>, action: LoginFailure) {

    if (isStudio) {
      this.pendoService.track('Sign In - Failure', { 
        type: action.loginType, 
        error: get(action, 'error.error.error.message') || 'There was an error logging in.'
      });
    }
  }

  @Action(Logout)
  logout(context: StateContext<AuthStateModel>, action: Logout) {
    context.patchState({ loading: true });

    this.authService.logout()
      .pipe(finalize(() => context.patchState({ loading: false })))
      .subscribe(
        () => context.dispatch(new LogoutSuccess()),
        () => context.dispatch(new LogoutFailure())
      );
  }

  @Action(LogoutSuccess)
  logoutSuccess(context: StateContext<AuthStateModel>, action: LogoutSuccess) {
    context.setState(initialState);

    context.dispatch(new ResetAuth());
    context.dispatch(new ResetSubscriptionState());
    context.dispatch(new ResetBookmarks());
    context.dispatch(new ResetEditPage());
    context.dispatch(new ResetEnginePosition());
    context.dispatch(new ResetLabels());
    context.dispatch(new LayoutActions.ResetLayout());
    context.dispatch(new ResetOrganizations());
    context.dispatch(new ResetStory());
    context.dispatch(new ResetStoryChapters());
    context.dispatch(new GalleryActions.Reset());    

    // clear cached storage
    this.storageService.clearSessionStorage();
  }

  @Action(CheckProfile)
  checkProfile(context: StateContext<AuthStateModel>, action: CheckProfile) {
    context.patchState({ loading: true });

    return this.authService.checkProfile(action.email)
      .pipe(finalize(() => context.patchState({ loading: false })))
      .subscribe(
        (profile) => {
          context.dispatch(new CheckProfileSuccess(profile));
        },
        (error) => {
          context.dispatch(new CheckProfileFailure(error));
        }
      );
  }

  @Action(CheckProfileSuccess)
  checkProfileSuccess(context: StateContext<AuthStateModel>, action: CheckProfileSuccess) {
    context.patchState({
      profileCheck: action.profile
    });
  }

  @Action(GetUserProfile)
  getUserProfile(context: StateContext<AuthStateModel>, { hardCheck }: GetUserProfile) {
    context.patchState({ loading: true });

    return this.authService.getUserProfile(hardCheck)
      .pipe(finalize(() => context.patchState({ loading: false })))
      .subscribe(
        (userProfile) => {
          context.dispatch(new GetUserProfileSuccess(userProfile));
        },
        (error) => {
          context.dispatch(new GetUserProfileFailure(error));
        }
      );
  }

  @Action(GetUserProfileSuccess)
  getUserProfileSuccess(context: StateContext<AuthStateModel>, action: GetUserProfileSuccess) {
    context.patchState({
      userProfile: action.userProfile
    });

    context.dispatch(new SetOrganizations(action.userProfile.organizations));
    context.dispatch(new GetUserApps());
    context.dispatch(new CheckLocale());
  }

  @Action(UpdateUserProfile)
  updateUserProfile(context: StateContext<AuthStateModel>, { userProfile }: UpdateUserProfile) {
    context.patchState({ profileStatus: 'updating' });

    this.authService.updaterUserProfile(userProfile)
      .pipe(finalize(() => context.patchState({ profileStatus: 'ready' })))
      .subscribe(
        () => {
          context.dispatch(new UpdateUserProfileSuccess(userProfile));
        },
        (error) => {
          context.dispatch(new UpdateUserProfileFailure(error));
        }
      );
  }

  @Action(DeleteUserProfile)
  deleteUserProfile(context: StateContext<AuthStateModel>, action: DeleteUserProfile) {
    context.patchState({ profileStatus: 'deleting' });

    this.authService.deleteUserProfile()
      .pipe(finalize(() => context.patchState({ profileStatus: 'ready' })))
      .subscribe(
        (email) => {
          context.dispatch(new DeleteUserProfileSuccess(email));
        },
        (error) => {
          context.dispatch(new DeleteUserProfileFailure(error));
        }
      );
  }

  @Action(GetUserRoles)
  getUserRoles(context: StateContext<AuthStateModel>, action: GetUserRoles) {
    this.authService.getUserRoles()
    .subscribe((userRoles)=> {
      context.patchState({
        userRoles
      })
    });
  }

  @Action(GetUserApps)
  getUserApps(context: StateContext<AuthStateModel>, action: GetUserApps) {
    this.authService.getUserApps()
      .subscribe(
        (apps) => {
          context.dispatch(new SetUserApps(apps));
          context.dispatch(new CheckSelectedUserApp());
        },
        // #TODO: handle errors
        (err) => {

        });
  }

  @Action(SetUserApps)
  setUserApps(context: StateContext<AuthStateModel>, { apps }: SetUserApps) {
    context.patchState({
      userApps: apps
    });
  }

  @Action(SetSelectedUserApp)
  setSelectedUserApp(context: StateContext<AuthStateModel>, { appId }: SetSelectedUserApp) {
    context.patchState({
      selectedAppId: appId
    });
    // update storage
    if (appId !== null) {
      this.storageService.setLocalItem(LocalStorageEnum.UserAppId, appId);
    } else {
      this.storageService.removeLocalItem(LocalStorageEnum.UserAppId);
    }
  }

  @Action(CheckSelectedUserApp)
  checkSelectedUserApp(context: StateContext<AuthStateModel>, action: CheckSelectedUserApp) {
    const apps = context.getState().userApps;
    const storedAppId = this.storageService.getLocalItem(LocalStorageEnum.UserAppId);
    if (storedAppId) { // set stored app
      const appId = parseInt(storedAppId, 10);
      context.dispatch(new SetSelectedUserApp(appId));
    } else if (apps && apps.length) { // set default app

      // restrict to web app
      const app = apps
        .find((a) => {
          return a.app_platform
            .some((p) => p.type === AppPlatformTypeEnum.Web);
        });

      context.dispatch(new SetSelectedUserApp(app ? app.id : undefined));
    }
  }

  @Action(CheckSession)
  checkSession(context: StateContext<AuthStateModel>, action: CheckSession) {
    context.patchState({ loading: true });

    this.authService.isLoggedIn()
      .pipe(finalize(() => context.patchState({ loading: false })))
      .subscribe((result: { signed_in: boolean }) => {
        if (result.signed_in) {
          context.dispatch(new IsLoggedInSuccess());
          context.dispatch(new GetUserProfile());
          context.dispatch(new GetUserRoles());
        } else {
          context.dispatch(new IsLoggedInFailure());
        }
      });
  }

  @Action(GetEnvironmentSettings)
  getEnvironmentSettings(context: StateContext<AuthStateModel>, action: GetEnvironmentSettings) {
    context.patchState({ loading: true });

    return this.authService.getEnvironmentSettings()
      .pipe(finalize(() => context.patchState({ loading: false })))
      .subscribe(
        (environmentSettings) => {
          context.dispatch(new GetEnvironmentSettingsSuccess(environmentSettings));
        },
        (error) => {
          context.dispatch(new GetEnvironmentSettingsFailure(error));
        }
      );
  }

  @Action(GetEnvironmentSettingsSuccess)
  GetEnvironmentSettingsSuccess(context: StateContext<AuthStateModel>, action: GetEnvironmentSettingsSuccess) {
    context.patchState({
      environmentSettings: action.environmentSettings
    });
  }

  @Action(ResetPassword)
  resetPassword(context: StateContext<AuthStateModel>, action: ResetPassword) {
    context.patchState({ loading: true });

    this.authService.resetPassword(action.email)
      .pipe(finalize(() => context.patchState({ loading: false })))
      .subscribe(
        () => {
          context.dispatch(new ResetPasswordSuccess(action.email));
        },
        (error) => {
          context.dispatch(new ResetPasswordFailure(error));
        }
      );
  }

  @Action(UpdatePassword)
  updatePassword(context: StateContext<AuthStateModel>, action: UpdatePassword) {
    const { currentPassword, newPassword, confirmNewPassword } = action;

    this.authService.updatePassword(currentPassword, newPassword, confirmNewPassword)
      .subscribe(
        () => {
          context.dispatch(new UpdatePasswordSuccess());
        },
        (error) => {
          context.dispatch(new UpdatePasswordFailure(error));
        }
      );
  }

  @Action(SignUp)
  signUp(context: StateContext<AuthStateModel>, action: SignUp) {
    context.patchState({ loading: true });

    this.verify('studio_signup', action.value.email, context)
      .pipe(
        mergeMap((verification: Verification) => {
          return this.authService.signUp({
            ...action.value,
            ...(verification) ? { site_verify_response: verification } : null
          });
        })
      ).subscribe((response: SignUpResponseModel) => {
        context.dispatch(new SignUpSuccess(response));
      },
      (error) => {
        context.dispatch(new SignUpFailure(error));
      });
  }

  @Action(SignUpSuccess)
  signUpSuccess(context: StateContext<AuthStateModel>, action: SignUpSuccess) {

    if (isStudio) {
      const queryParams = this.route.snapshot.queryParams;
      
      this.pendoService.track('Sign Up - Success', {
        referrer: document.referrer,
        refParam: queryParams.ref || null,
        groupInvite: queryParams.hasOwnProperty('group_invite'),
        groupCode: queryParams.hasOwnProperty('group') && queryParams.hasOwnProperty('code')
      });
    }
  }

  @Action(SignUpFailure)
  signUpFailure(context: StateContext<AuthStateModel>, action: SignUpFailure) {

    if (isStudio) {
      this.pendoService.track('Sign Up - Failure', {
        error: get(action, 'error.error.error.message') || 'There was an error signing up.'
      });
    }

    this.logFailure('BIODIGITAL_SIGNUP_FAILURE', action);
  }

  @Action(SetUserLocale)
  setUserLocale(context: StateContext<AuthStateModel>, { locale }: SetUserLocale) {
    // set storage
    this.storageService.setLocalItem(LocalStorageEnum.Locale, locale.id);

    context.patchState({ userLocale: locale });
    context.dispatch(new SetUserLocaleSuccess(locale));
  }

  @Action(CheckLocale)
  checkLocale(context: StateContext<AuthStateModel>, action: CheckLocale) {
    const targetLocale = locales.find((locale) => locale.id === this.localeId) || defaultLocale;
    context.dispatch(new SetUserLocale(targetLocale));
  }

  @Action(SendUserMessage)
  sendUserMessage(context: StateContext<AuthStateModel>, { message }: SendUserMessage) {
    this.userMessageService.sendMessage(message)
      .subscribe(
        () => {
          context.dispatch(new SendUserMessageSuccess(message));
        },
        (err) => {
          context.dispatch(new SendUserMessageFail(err));
        });
  } 

  @Action(GetUserMessage)
  getUserMessage(context: StateContext<AuthStateModel>, { type, mode }: GetUserMessage) {
    this.userMessageService.getUserMessage(type, mode)
      .subscribe(
        (message) => {
          context.dispatch(new GetUserMessageSuccess(message));
        },
        () => {
          context.dispatch(new GetUserMessageFail(type));
        });
  } 

  @Action(SetUserMessageViewed)
  setUserMessageViewed(context: StateContext<AuthStateModel>, { messageSeenId }: SetUserMessageViewed) {
    this.userMessageService.setUserMessageViewed(messageSeenId)
      .subscribe(
        () => context.dispatch(new SetUserMessageViewedSuccess()),
        () => context.dispatch(new SetUserMessageViewedFail())
      );
  }  

  @Action(ResetAuth)
  resetAuth(context: StateContext<AuthStateModel>, action: ResetAuth) {
    context.setState(initialState);
  }

  private logFailure(category: string, error: any) {
    const message = get(error, 'message') || '';
    const log = get(error, 'error.error.message') || '';
    this.errorLoggingService.logError(new ErrorMessage(category, message, log)).subscribe();
  }

  private verify(action: string, email: string, context: StateContext<AuthStateModel>): Observable<Verification> {
    // Use reCAPTCHA to verify user, unless:
    // - The environment is localhost
    // - The email is whitelisted. This occurs when some clients experience issues with reCAPTCHA due to services
    //   like ezProxy.
    return (!this.authService.isReCaptchaWhiteListed(email) && environment.deploy !== EnvironmentDeploy.Local) ? 
      this.authService.isHuman(action)
        .pipe(
          tap((response: HumanVerificationModel) => { if (!response.isHuman) {
            context.dispatch(new VerifyFail());
          }}),
          filter((response: HumanVerificationModel) => response.isHuman),
          map((response: HumanVerificationModel) => response.verification)
        )
      : of(null);
  }

  constructor(
    private route: ActivatedRoute,
    private authService: AuthService,
    private userMessageService: UserMessageService,
    private storageService: StorageService,
    @Inject(LOCALE_ID) private localeId: string,
    private errorLoggingService: ErrorLoggingService,
    private facebookPixel: FacebookPixelService,
    private linkedinInsights: LinkedinInsightsService,
    private pendoService: PendoService
  ) { }

  ngxsOnInit(context: StateContext<AuthStateModel>) {
    context.dispatch(new CheckSession());
    context.dispatch(new CheckLocale());
    context.dispatch(new CheckSelectedUserApp());
  }
}
